import type { Article, Category, Topic, TagItem } from '@/types/news';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import { topics } from '@/mocks/topics';
import { hotTags } from '@/mocks/tags';

export const allArticles = articles as Article[];
export const allCategories = categories as Category[];
export const allTopics = topics as Topic[];
export const allTags = hotTags as TagItem[];

export function parseTime(value: string): number {
  return new Date(value.replace(/-/g, '/')).getTime();
}

export function getCategoryBySlug(slug: string): Category | undefined {
  return allCategories.find((item) => item.slug === slug);
}

export function getArticleById(id: string): Article | undefined {
  return allArticles.find((item) => item.id === id);
}

export function getArticlesByCategory(slug: string): Article[] {
  return allArticles.filter((item) => item.category === slug);
}

export function sortByTime(list: Article[]): Article[] {
  return [...list].sort((a, b) => parseTime(b.publishTime) - parseTime(a.publishTime));
}

export function sortByHot(list: Article[]): Article[] {
  return [...list].sort((a, b) => b.hot - a.hot);
}

export function getLatestArticles(limit?: number): Article[] {
  const sorted = sortByTime(allArticles);
  return typeof limit === 'number' ? sorted.slice(0, limit) : sorted;
}

export function getHotArticles(limit?: number): Article[] {
  const sorted = sortByHot(allArticles);
  return typeof limit === 'number' ? sorted.slice(0, limit) : sorted;
}

export function searchArticles(keyword: string): Article[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return allArticles.filter((item) => {
    const haystack = [
      item.title,
      item.summary,
      item.categoryName,
      item.source,
      item.author,
      item.tags.join(' '),
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(kw);
  });
}

export function getRelatedArticles(article: Article, limit = 5): Article[] {
  const scored = allArticles
    .filter((item) => item.id !== article.id)
    .map((item) => {
      let score = 0;
      if (item.category === article.category) score += 3;
      score += item.tags.filter((tag) => article.tags.includes(tag)).length * 2;
      return { item, score };
    })
    .sort((a, b) => b.score - a.score || b.item.hot - a.item.hot);
  return scored.slice(0, limit).map((entry) => entry.item);
}

export function getArticlesByTag(tag: string): Article[] {
  return allArticles.filter((item) => item.tags.includes(tag));
}

export function formatNumber(value: number): string {
  if (value >= 100000000) return `${(value / 100000000).toFixed(1)}亿`;
  if (value >= 10000) return `${(value / 10000).toFixed(1)}万`;
  return String(value);
}

export function formatDate(value: string): string {
  return value.slice(0, 10);
}

export function timeAgo(value: string): string {
  const diff = Date.now() - parseTime(value);
  if (diff < 0) return '刚刚';
  const hour = 3600000;
  const day = 86400000;
  if (diff < hour) return `${Math.max(1, Math.floor(diff / 60000))}分钟前`;
  if (diff < day) return `${Math.floor(diff / hour)}小时前`;
  return `${Math.floor(diff / day)}天前`;
}